/****************************************************
 * Module Name:  evaluationModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Button, Container, Tooltip, Avatar, Chip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import CommonSnackbar from "./commonSnackBar";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import logo from '../../images/pansuriya.jpg'
import "./../../style/modalstyle.css";
import PacketEvaluationProxy from "../../utility/proxy/manager/manufacturingProxy/packetEvaluationProxy";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PacketHistoryModal from "./packetHistoryModal";

const EvaluationModal = ({ kapanId, packetId, handleClose, show }) => {
    const [data, setData] = useState([]);
    const [detailLoader, setDetailLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);


    useEffect(() => {
        handleSearch(kapanId, packetId);
    }, []);

    const handleSearch = (kapanId, packetId) => {
        setDetailLoader(true);
        PacketEvaluationProxy.GetAllEvaluationData(
            kapanId, packetId,
            (responseData) => {
                if (responseData) {
                    let data = responseData
                    for (let i = 0; i < data.length; i++) {
                        data[i].id = i + 1
                    }
                    setData(data)
                    setDetailLoader(false)
                } else {
                    setDetailLoader(false)
                    setErrorSnackbar(true)
                }
            }
        );
    };

    const handleKapanDetailModal = (params) => {
        setShowKapanDetailModal(true);
    };

    const initialColumns = () => [
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleKapanDetailModal(params.value)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setShowPacketDetailModal(true)
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "userid",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.userid ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.value}</Avatar>}
                            label={params.row.username}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                    : '-'
            ),
            disableExport: true,
        },
        {
            field: "departmentid",
            headerName: "DEPARTMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.departmentid ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.value}</Avatar>}
                            label={params.row.departmentname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                    : '-'
            ),
            disableExport: true,
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 190,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "right",
            width: 40,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            renderCell: renderCellExpand,
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        {
            field: "repacode",
            headerName: "REPA CODE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            renderCell: renderCellExpand,
        },
        {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            width: 70,
            renderCell: renderCellExpand,
        },
        {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            renderCell: renderCellExpand,
        },
        {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 170,
            renderCell: renderCellExpand,
        },
        {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            renderCell: renderCellExpand,
        },
        {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            renderCell: renderCellExpand,
        },
        {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: renderCellExpand,
        },
        {
            field: "girdlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            minWidth: 170,
            renderCell: renderCellExpand,
        },
        {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            cellClassName: (params) => Utility.highlightLasertInscriptionCell(params.value)
        },
        {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: renderCellExpand,
        },
        {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            minWidth: 110,
            renderCell: renderCellExpand,
        },
        {
            field: "girdlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            minWidth: 110,
            renderCell: renderCellExpand,
        },
        {
            field: "girdlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            align: "center",
            minWidth: 110,
            renderCell: renderCellExpand,
        },
        {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            minWidth: 110,
            renderCell: renderCellExpand,
        },
        {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            minWidth: 110,
            renderCell: renderCellExpand,
        },
        {
            field: "sizemastreid",
            headerName: "SIZE MASTER ID",
            headerAlign: "center",
            align: "center",
            minWidth: 110,
            renderCell: renderCellExpand,
        },

    ];

    const rows = data;
    const columns = initialColumns();
    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}
            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={() => setShowPacketDetailModal(false)}
                />
            ) : null}
            <Box>
                <Modal show={show} onHide={handleClose} dialogClassName="bhb" enforceFocus={false}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;Evaluation Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Container maxWidth="xxl">
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    width: "100%",
                                    position: "relative",
                                    "& .MuiTablePagination-selectLabel": {
                                        marginTop: "15px",
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        marginTop: "15px",
                                    },
                                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                        color: "#1976d2",
                                        padding: "4px 5px",
                                        borderRadius: "4px",
                                    },
                                    "& .css-cc8tf1": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                        color: "#1976d2",
                                        padding: "5px 5px",
                                        borderRadius: "4px",
                                    },
                                    height: "50vh",
                                }}
                            >
                                <DataGridPro
                                    disableRowSelectionOnClick={true}
                                    getRowHeight={() => "auto"}
                                    sx={{
                                        '& .MuiDataGrid-columnHeader': {
                                            color: '#fff',
                                            backgroundColor: 'grey',
                                        },
                                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                            py: "8px",
                                        },
                                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                            py: "15px",
                                        },
                                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                            py: "22px",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                                            width: "0.4em",
                                            height: "0.7em",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                                            background: "#f1f1f1",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#888",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
                                            background: "#555",
                                        },
                                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                    }}
                                    density="compact"
                                    slots={{ toolbar: GridToolbar }}
                                    rows={rows}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                // Hide columns id, the other columns will remain visible
                                                id: false,
                                            },
                                        },
                                        pagination: {
                                            paginationModel: Constants.pageSize,
                                        },
                                        sorting: {
                                            sortModel: [{ field: "createdat", sort: "desc" }],
                                        },
                                    }}
                                    pageSizeOptions={Constants.pageSizeOptions}
                                    pagination
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 },
                                        },
                                    }}
                                />
                                <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                    {detailLoader ? (
                                        <CircularProgress size="1rem" />
                                    ) : (
                                        <Tooltip title="Reload List">
                                            <RefreshOutlinedIcon
                                                onClick={() => handleSearch(kapanId, packetId)}
                                                color="primary"
                                                fontSize="medium"
                                                sx={{ cursor: "pointer", mr: 1 }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                        </Container>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="contained" color="error" onClick={handleClose}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Box>

        </div>
    );
};

export default EvaluationModal;
